import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  Heading,
  SimpleGrid,
  useColorModeValue,
  useToast,
  Divider,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";

import { useNavigate, useParams } from "react-router-dom";
import {
  getTicketById,
  updateTicket,
  fetchUsers,
} from "../logic/ticketLogic";
import { fetchCategories } from "../logic/categoryLogic";

export default function FormPenyelesaian() {
  const cardBg = useColorModeValue("white", "navy.700");
  const toast = useToast();
  const navigate = useNavigate();
  const { ticketId } = useParams();

  const token = localStorage.getItem("token");

  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [comments, setComments] = useState([]);

  const [form, setForm] = useState({
    subject: "",
    description: "",
    user_id: "",
    category_id: "",
    assigned_to: "",
    urgensi: "rendah",
  });

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  // Confirm dialog
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  // =========================
  // LOAD DATA
  // =========================
  useEffect(() => {
    const loadData = async () => {
      try {
        const usersData = await fetchUsers(token);
        setUsers(usersData);

        const categoriesData = await fetchCategories(token);
        setCategories(categoriesData);

        const ticket = await getTicketById(ticketId, token);

        setForm({
          subject: ticket.subject,
          description: ticket.description,
          user_id: ticket.user_id,
          category_id: ticket.category_id,
          assigned_to: ticket.assigned_to,
          urgensi: ticket.urgensi,
        });

        setComments(ticket.comments || []);
      } catch (error) {
        toast({
          title: "Gagal memuat detail ticket",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setPageLoading(false);
      }
    };

    loadData();
  }, [ticketId, token, toast]);

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  // =========================
  // EXECUTE PENYELESAIAN
  // =========================
  const handleConfirmSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        status: "selesai",
      };

      await updateTicket(ticketId, payload, token);

      toast({
        title: "Ticket berhasil diselesaikan",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate("/admin/dashboard-teknisi");
    } catch (error) {
      toast({
        title: "Gagal menyelesaikan ticket",
        status: "error",
      });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  if (pageLoading) return <Box p="20px">Loading...</Box>;

  return (
    <Box bg={cardBg} p="30px" borderRadius="20px" boxShadow="sm">
      <Heading size="md" mb="20px">
        Form Penyelesaian Ticket #{ticketId}
      </Heading>

      <VStack spacing="20px" align="stretch">
        <FormControl>
          <FormLabel>Subject</FormLabel>
          <Input
            name="subject"
            value={form.subject}
            onChange={handleChange}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Description</FormLabel>
          <Textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={5}
          />
        </FormControl>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
          <FormControl>
            <FormLabel>Karyawan</FormLabel>
            <Select
              name="user_id"
              value={form.user_id}
              onChange={handleChange}
            >
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Category</FormLabel>
            <Select
              name="category_id"
              value={form.category_id}
              onChange={handleChange}
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </FormControl>
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
          <FormControl>
            <FormLabel>Assigned To</FormLabel>
            <Select
              name="assigned_to"
              value={form.assigned_to}
              onChange={handleChange}
            >
              <option value="">-</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Urgensi</FormLabel>
            <Select
              name="urgensi"
              value={form.urgensi}
              onChange={handleChange}
            >
              <option value="rendah">Rendah</option>
              <option value="sedang">Sedang</option>
              <option value="tinggi">Tinggi</option>
            </Select>
          </FormControl>
        </SimpleGrid>

        {/* BUTTON */}
        <Box display="flex" justifyContent="flex-end" mt="10px">
          <Button
            colorScheme="green"
            size="md"
            px="30px"
            onClick={onOpen}
          >
            Tandai Selesai
          </Button>
        </Box>

        {/* ============================= */}
        {/* HISTORI AKTIVITAS (ACCORDION) */}
        {/* ============================= */}
        <Divider mt="30px" />

        <Accordion allowToggle>
          <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="8px">
            <AccordionButton>
              <Box flex="1" textAlign="left" fontWeight="semibold">
                Histori Aktivitas ({comments.length})
              </Box>
              <AccordionIcon />
            </AccordionButton>

            <AccordionPanel pb={4}>
              <Box maxH="300px" overflowY="auto">
                {comments.map((c) => (
                  <Box
                    key={c.id}
                    p="10px"
                    mb="10px"
                    bg="gray.50"
                    borderRadius="8px"
                  >
                    <Text fontWeight="bold">{c.user?.name}</Text>
                    <Text fontSize="sm" color="gray.500">
                      {new Date(c.created_at).toLocaleString()}
                    </Text>
                    <Text mt="5px">{c.message}</Text>
                  </Box>
                ))}
              </Box>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </VStack>

      {/* ============================= */}
      {/* CONFIRM DIALOG */}
      {/* ============================= */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Konfirmasi Penyelesaian
            </AlertDialogHeader>

            <AlertDialogBody>
              Apakah kamu yakin ingin menandai ticket ini sebagai <b>SELESAI</b>?
              Setelah diselesaikan, status tidak dapat dikembalikan.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Batal
              </Button>
              <Button
                colorScheme="green"
                onClick={handleConfirmSubmit}
                ml={3}
                isLoading={loading}
              >
                Ya, Selesaikan
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
